import { useQuery } from '@apollo/client';
import type { QueryResult } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import * as _ from 'lodash-es';
import { Delta } from 'quill/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CommentResultCard from './CommentResultCard';
import PostResultCard from './PostResultCard';
import SearchResultCards from './SearchResultCards';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import { SearchFilterType } from '../graphql/generated/graphql';
import GroupResultCard from '../groups/GroupResultCard';
import TopicResultCard from '../topic/TopicResultCard';
import UserResultCard from '../user/UserResultCard';
import ConnectionHelper, {
  offsetToCursor,
} from '../utils/graphql/ConnectionHelper';

const PREVIEW_SLAB_RESULT_COUNT = 10;
// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const FULL_SLAB_RESULT_COUNT = 50;
// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const SLAB_RESULT_PAGE_SIZE = 25;

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const SLAB_SEARCH_QUERY = gql(/** @complexity 68 */ `
  query SearchViewSlabSearch(
    $query: String!
    $first: Int!
    $afterCursor: String
    $filter: SearchFilter
  ) {
    search(
      query: $query
      first: $first
      after: $afterCursor
      filter: $filter
    ) {
      edges {
        node {
          ... on PostSearchResult {
            content
            highlight
            highlightIndex
            title
            post {
              ...PostResultCard_post
            }
            ancestors {
              id
              ...PostResultCard_ancestors
            }
            resultId
          }
          ... on TopicSearchResult {
            name
            description
            topic {
              ...TopicResultCard_topic
            }
            ancestors {
              id
              ...TopicResultCard_ancestors
            }
          }
          ... on UserSearchResult {
            description
            name
            title
            user {
              ...UserResultCard_user
            }
          }
          ... on CommentSearchResult {
            content
            comment {
              id
              ...CommentResultCard_comment
            }
          }
          ... on GroupSearchResult {
            name
            description
            group {
              id
              ...GroupResultCard_group
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      queryId
    }
  }
`);

const typeMap = _.invert({
  [SearchFilterType.Post]: 'post',
  [SearchFilterType.User]: 'user',
  [SearchFilterType.Comment]: 'comment',
  [SearchFilterType.Template]: 'template',
  [SearchFilterType.Topic]: 'topic',
  [SearchFilterType.Group]: 'group',
} satisfies Record<SearchFilterType, string>);

const parseType = (type: string | undefined) =>
  type ? (typeMap[type] as SearchFilterType | undefined) : null;

const useInitialFullCountFetch = (
  response: QueryResult<
    DocumentType<typeof SLAB_SEARCH_QUERY>,
    VariablesOf<typeof SLAB_SEARCH_QUERY>
  >,
  {
    previewCount,
    fullCount,
  }: {
    previewCount: number;
    fullCount: number;
  },
) => {
  const hasInitiallyFetchedMoreRef = useRef(false);
  const [isFetchMoreAllowed, setIsFetchMoreAllowed] = useState(false);

  const hasNextPage = response.data?.search?.pageInfo?.hasNextPage ?? false;
  const resultsCount = response.data?.search?.edges?.length ?? 0;
  const { fetchMore } = response;

  useEffect(() => {
    if (hasInitiallyFetchedMoreRef.current) return;

    if (hasNextPage && resultsCount <= previewCount) {
      fetchMore({
        variables: {
          first: fullCount - previewCount,

          // Sometimes the backend might return fewer results and still have a next page.
          // This is due to filtering. In that case, we still need to set the afterCursor
          // to the very end of the page and not use the endCursor, because otherwise,
          // we might include the same results twice.
          //
          // Example: filtered results are 0,8,9, but the endCursor is arrayconnection:2.
          // If we would use the endCursor, we would start at 2 and so
          // the result would include 8 and 9 again.
          afterCursor: offsetToCursor(previewCount - 1),
        },
      }).then(() => setIsFetchMoreAllowed(true));
      hasInitiallyFetchedMoreRef.current = true;
    }
  }, [fetchMore, hasNextPage, resultsCount, previewCount, fullCount]);

  return isFetchMoreAllowed;
};

const useFetchMore = (
  response: QueryResult<
    DocumentType<typeof SLAB_SEARCH_QUERY>,
    VariablesOf<typeof SLAB_SEARCH_QUERY>
  >,
  {
    fullCount,
    pageSize,
  }: {
    fullCount: number;
    pageSize: number;
  },
) => {
  const [pageNumber, setPageNumber] = useState(0);

  const fetchMoreQuery = ConnectionHelper.generateFetchMore(
    SLAB_SEARCH_QUERY,
    response,
    'search',
    {
      afterCursorOffset: fullCount + pageSize * pageNumber - 1,
    },
  );

  return (
    fetchMoreQuery &&
    (async () => {
      const results = await fetchMoreQuery({ first: pageSize });
      setPageNumber((currentPageNumber) => currentPageNumber + 1);
      return results;
    })
  );
};

export type SearchVariables = Omit<
  VariablesOf<typeof SLAB_SEARCH_QUERY>,
  'first'
>;

interface InnerSearchViewSlabSearchProps {
  variables: SearchVariables;
  type?: string;
  active: boolean;
}

const InnerSearchViewSlabSearch = ({
  active,
  variables,
  type,
}: InnerSearchViewSlabSearchProps) => {
  const [hasBeenActive, setHasBeenActive] = useState(active);

  // we want to skip the query only the first time when not active,
  // because skipping and unskipping would trigger a refetch
  if (active && !hasBeenActive) {
    setHasBeenActive(true);
  }

  const filter = useMemo(() => {
    const parsedType = parseType(type);
    return parsedType
      ? { ...variables.filter, types: [parsedType] }
      : variables.filter;
  }, [type, variables.filter]);

  const response = useQuery(SLAB_SEARCH_QUERY, {
    fetchPolicy: 'network-only',
    skip: !hasBeenActive,
    variables: {
      ...variables,
      filter,

      // We first load a preview of the results
      first: PREVIEW_SLAB_RESULT_COUNT,
    },
  });

  // Then, we initially load more results up to the full result count
  const isFetchMoreAllowed = useInitialFullCountFetch(response, {
    fullCount: FULL_SLAB_RESULT_COUNT,
    previewCount: PREVIEW_SLAB_RESULT_COUNT,
  });

  // This function can then be used to load the next page after the full result count
  const fetchMore = useFetchMore(response, {
    fullCount: FULL_SLAB_RESULT_COUNT,
    pageSize: SLAB_RESULT_PAGE_SIZE,
  });

  const data = useMemo(
    () => _.compact(response.data?.search?.edges?.map((edge) => edge?.node)),
    [response.data?.search?.edges],
  );

  if (!active) return null;

  if (!data.length) {
    return response.loading ? null : (
      <EmptyState
        imageName="notFound"
        title="No results found"
        message="Try another search with fewer keywords"
      />
    );
  }

  return (
    <>
      <SearchResultCards>
        {data.map((result, index) => {
          if ('post' in result) {
            const {
              highlight,
              highlightIndex,
              post,
              title,
              ancestors,
              resultId,
            } = result;

            const highlightDelta = new Delta(highlight);
            const content = new Delta(result.content).compose(highlightDelta);

            return (
              <PostResultCard
                key={post.id}
                post={post}
                title={title}
                content={content}
                queryId={response.data?.search?.queryId ?? undefined}
                resultId={resultId ?? undefined}
                position={index + 1}
                highlight={highlightDelta}
                highlightIndex={highlightIndex || undefined}
                ancestors={ancestors}
              />
            );
          }
          if ('topic' in result) {
            const { topic, name, description, ancestors } = result;

            return (
              <TopicResultCard
                key={result.topic.id}
                topic={topic}
                name={name}
                description={description}
                ancestors={ancestors}
              />
            );
          }
          if ('user' in result) {
            const { name, title, description, user } = result;

            return (
              <UserResultCard
                key={result.user.id}
                user={user}
                name={name}
                title={title}
                description={description}
              />
            );
          }

          if ('comment' in result) {
            return (
              <CommentResultCard
                key={result.comment.id}
                comment={result.comment}
                content={result.content}
              />
            );
          }

          if ('group' in result) {
            const { name, group, description } = result;

            return (
              <GroupResultCard
                key={group.id}
                group={{
                  ...group,
                  name: name ?? group.name,
                  description: description ?? group.description,
                }}
              />
            );
          }
          return null;
        })}
      </SearchResultCards>
      {isFetchMoreAllowed && <InfiniteScrollWaypoint fetchMore={fetchMore} />}
    </>
  );
};

type SearchViewSlabSearchProps = InnerSearchViewSlabSearchProps;

const SearchViewSlabSearch = (props: SearchViewSlabSearchProps) => {
  const [variablesEpoch, setVariablesEpoch] = useState(0);
  const [previousVariables, setPreviousVariables] = useState(props.variables);

  if (!_.isEqual(props.variables, previousVariables)) {
    setPreviousVariables(props.variables);
    setVariablesEpoch((currentEpoch) => currentEpoch + 1);
  }

  return <InnerSearchViewSlabSearch key={variablesEpoch} {...props} />;
};

export default SearchViewSlabSearch;
